import React, { useCallback, useState, useMemo } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { Flex, Text } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import {
  DistributionBatchesTable,
  DistributionWorkflow,
  DistributionBatchRowItem,
  PaymentBatchRowItem,
} from "@jsq/payments-components";
import { useAppContext } from "~/contexts/appContext";
import { globalEnv } from "~/global-env";
import { toGlobalId } from "~/utils/graphql-helpers";
import { hasTreasuryAccessOnAnyArena, isTreasuryUser } from "~/utils/userRoles";
import * as styles from "./styles_vanilla.css";

// Route param types
type DistributionsPageParams = {
  transactionBatchId?: string;
  paymentBatchId?: string;
};
type PayWorkflowParams = { distributionBatchId: string };
type ReviewWorkflowParams = { distributionBatchId: string; paymentBatchId: string };

const DistributionsPage: React.FC = () => {
  const history = useHistory();
  const { transactionBatchId: distributionBatchId, paymentBatchId } =
    useParams<DistributionsPageParams>();
  const [selectedBatch, setSelectedBatch] = useState<DistributionBatchRowItem | null>(null);
  const { user } = useAppContext();

  // Match against /pay route
  const payMatch = useRouteMatch<PayWorkflowParams>({
    path: "/treasury/transactions/:distributionBatchId/pay",
    exact: true,
  });

  // Match against /review route
  const reviewMatch = useRouteMatch<ReviewWorkflowParams>({
    path: "/treasury/transactions/:distributionBatchId/:paymentBatchId/review",
    exact: true,
  });

  // Determine which workflow to show
  const showPayWorkflow = !!payMatch;
  const showReviewWorkflow = !!reviewMatch;

  // Convert database ID to global ID for components that need it
  const distributionBatchGlobalId = distributionBatchId
    ? toGlobalId("DistributionBatchObjectType", distributionBatchId)
    : undefined;

  // Parse database ID from URL param for DistributionWorkflow
  const selectedDistributionBatchIdNumber = distributionBatchId
    ? parseInt(distributionBatchId, 10)
    : null;

  const handleSelectDistributionBatch = useCallback(
    (distributionBatch: DistributionBatchRowItem) => {
      setSelectedBatch(distributionBatch);
      // Navigate to open the slideout via URL
      history.push(`${globalEnv.treasuryRoute}/transactions/${distributionBatch.id}`);
    },
    [history],
  );

  const handlePayClick = useCallback(
    (batch: DistributionBatchRowItem) => {
      setSelectedBatch(batch);
      history.push(`${globalEnv.treasuryRoute}/transactions/${batch.id}/pay`);
    },
    [history],
  );

  const handleDistributionSlideoutClose = useCallback(() => {
    setSelectedBatch(null);
    history.push(`${globalEnv.treasuryRoute}/transactions`);
  }, [history]);

  const handlePayWorkflowExit = useCallback(() => {
    setSelectedBatch(null);
    history.goBack();
  }, [history]);
  const handlePaymentBatchReviewClick = useCallback(
    (paymentBatch: PaymentBatchRowItem) => {
      if (!distributionBatchId || !paymentBatch.id) return;
      history.push(
        `${globalEnv.treasuryRoute}/transactions/${distributionBatchId}/${paymentBatch.id}/review`,
      );
    },
    [history, distributionBatchId],
  );

  const handleDistributionBatchReviewClick = useCallback(
    (distributionBatch: DistributionBatchRowItem) => {
      // For distribution batches with review action, navigate directly to the first payment batch review
      const firstPaymentBatchId = distributionBatch.paymentBatchesForReview[0];
      if (firstPaymentBatchId) {
        history.push(
          `${globalEnv.treasuryRoute}/transactions/${distributionBatch.id}/${firstPaymentBatchId}/review`,
        );
      } else {
        // Fallback: just open the distribution slideout if no payment batches
        history.push(`${globalEnv.treasuryRoute}/transactions/${distributionBatch.id}`);
      }
    },
    [history],
  );

  const handleReviewSlideoutClose = useCallback(() => {
    // Navigate back to where we came from (either distribution slideout or main table)
    history.goBack();
  }, [history]);

  const clientLevelPermissionsEnabled = useFeatureFlag("treasuryClientLevelPermissions", false);

  const paymentsContext = useMemo(
    () => ({
      isGPX: false,
      // all canopy treasury users can approve payments from a permissions perspective
      hasApprovePaymentPermission: clientLevelPermissionsEnabled
        ? hasTreasuryAccessOnAnyArena(user.roles)
        : isTreasuryUser(user.roles),
      currentUserId: user.id,
    }),
    [user.id, user.roles, clientLevelPermissionsEnabled],
  );

  return (
    <>
      <Flex className={styles.DistributionsPageContainer}>
        <Flex className={styles.DistributionsHeader}>
          <Text variant="h2">Transactions</Text>
        </Flex>
        <DistributionBatchesTable
          paymentsContext={paymentsContext}
          selectedDistributionBatchId={showPayWorkflow ? undefined : distributionBatchGlobalId}
          onSelectDistributionBatch={handleSelectDistributionBatch}
          onCloseSlideout={handleDistributionSlideoutClose}
          onPayClick={handlePayClick}
          onReviewClick={handleDistributionBatchReviewClick}
          onPaymentBatchReviewClick={handlePaymentBatchReviewClick}
          selectedPaymentBatchId={showReviewWorkflow ? paymentBatchId : undefined}
          onReviewSlideoutClose={handleReviewSlideoutClose}
          showActionColumn
        />
      </Flex>
      {showPayWorkflow && selectedDistributionBatchIdNumber && (
        <DistributionWorkflow
          distributionBatchId={selectedDistributionBatchIdNumber}
          clientName={selectedBatch?.clientName ?? ""}
          investmentEntityName={selectedBatch?.entityName ?? ""}
          onExit={handlePayWorkflowExit}
        />
      )}
    </>
  );
};

export default DistributionsPage;
